import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import InView from 'react-intersection-observer';
import { useReactiveVar } from '@apollo/client';

import {
  updateSelectedPlan,
  usePersistentplanAndFreq,
} from 'store/planAndFreq';
import {
  ICountryList,
  TfrequenciesV2,
  allFiltersType,
  getCategorizedProductsType,
  plansType,
} from '@utils/types';
import { AppRoutes } from '@utils/appRoutes';
import CountryDrawer from '@components/CountryDrawer';
import RedirectToIndiaModal from '@containers/HomePage/RedirectToIndiaModal';
import {
  countryCodeForEventVar,
  isABTestToFiredVar,
  isComeFromRedirectVar,
  isNewOrderFlowVar,
  planAbTestNameVar,
  stepTwoplanAbTestVar,
  subscriptionIdStateVar,
  utmQueryVar,
} from 'store/utils';
import { usePersistentPromoCodeDetails } from 'store/promoCode';
import useUpdateBannerCopyWithPromo from '@utils/useUpdateBannerCopyWithPromo';
import { INDIA_BASE_URL } from '@utils/data';

import ProductCategories from './Categories';
import Header from './Header';
import PlanAndFreq from './PlanAndFreq';
import PageLoader from '@components/PageLoader';
import { fetchPlanAndFreqVar } from 'store/planAndFreqnew';
import useAuth from '@utils/useAuth';
import { useElevar } from '@utils/elevar';
import { allvarinatMappingVar } from 'store/productsFlatList';
import { countryDetailVar } from 'store/country';
import { useGetSideDetailQuery } from 'commonModules/orderFlow';

interface ICombinedOrderStepOneAndTwo {
  getServiceableCountries?: ICountryList[];
  plans: plansType[];
  categories: getCategorizedProductsType[];
  getAllFilters: allFiltersType[];
  countryCode: string;
  frequencies: string[];
  frequenciesV2: TfrequenciesV2[];
  allVariantMapping: any;
}

const CombinedOrderStepOneAndTwo = ({
  getServiceableCountries,
  plans,
  categories,
  getAllFilters,
  countryCode,
  frequencies,
  frequenciesV2,
  allVariantMapping,
}: ICombinedOrderStepOneAndTwo) => {
  const [isCountryDrawerOpen, setCountryDrawerOpen] = useState(false);
  const [isHeaderExpanded, setHeaderExpanded] = useState<boolean>(false);
  const [defaultCountry, setDefaultCountry] = useState<ICountryList>();
  const [isLoading, setLoading] = useState<boolean>(false);

  const router = useRouter();

  const { authState } = useAuth();
  const { elevarUserDataEvent } = useElevar();

  const planAndFreq = usePersistentplanAndFreq();

  const planAndFreqVar = useReactiveVar(fetchPlanAndFreqVar);

  const selectedPlanId = planAndFreq?.selectedPlanId;
  const selectedPlan = planAndFreqVar?.plans?.[0]?.find(
    (i) => i.id === selectedPlanId
  );

  const { updatePromoCodeDetails } = useUpdateBannerCopyWithPromo();
  useGetSideDetailQuery({ countryCode });

  const { new_order_flow, landing_page_variant } = router.query;

  const promoCodeDetails = usePersistentPromoCodeDetails();

  const { isPromoValid, promoCode, amountType, amount } =
    promoCodeDetails || {};

  const planAbTestName = useReactiveVar(planAbTestNameVar);
  const isPlanAbTestFired = useReactiveVar(stepTwoplanAbTestVar);
  const isABTestFired = useReactiveVar(isABTestToFiredVar);

  useEffect(() => {
    if (plans) {
      fetchPlanAndFreqVar({
        plans,
        frequencies,
        frequenciesV2,
      });
    }
  }, [plans]);

  useEffect(() => {
    if (countryCode) {
      countryCodeForEventVar(countryCode);
    }
  }, [countryCode]);

  // elevar dl_user_data
  useEffect(() => {
    if (authState && selectedPlan) {
      setTimeout(() => {
        elevarUserDataEvent();
      }, 500);
    }
  }, [authState.token, selectedPlan?.id]);

  useEffect(() => {
    //@ts-ignore
    window?.dataLayer.push({ event: 'step_2' });

    // elevar
    //@ts-ignore
    window.ElevarInvalidateContext?.();
  }, []);

  useEffect(() => {
    if (Object.keys(router?.query).length) {
      utmQueryVar(router?.query);
    }
  }, []);

  useEffect(() => {
    if (allVariantMapping) {
      allvarinatMappingVar(allVariantMapping);
    }
  }, [allVariantMapping]);

  useEffect(() => {
    if (getServiceableCountries) {
      countryDetailVar(getServiceableCountries);
    }
  }, [getServiceableCountries]);

  useEffect(() => {
    // ABC test
    if (landing_page_variant) {
      planAbTestNameVar(landing_page_variant as string);
      isABTestToFiredVar(true);
    }
  }, [landing_page_variant]);

  // for update the promocode detail on direct landing on step 2 with promo query and update promodetails when landing on home page with promo i.e free-meals and free-gifts as it need planid
  useEffect(() => {
    if (
      router?.query?.promo ||
      (!router?.query?.promo &&
        (amountType === 'FREE_MEALS' || amountType === 'FREE_GIFTS') &&
        isPromoValid &&
        promoCode)
    ) {
      // to update banner as well store promo to global store
      updatePromoCodeDetails({
        promoCode: (router.query.promo as string) || promoCode,
        countryCode,
        planId: planAndFreq?.selectedPlanId,
      });
    }
  }, [
    router?.query,
    countryCode,
    planAndFreq?.selectedPlanId,
    amountType === 'FREE_MEALS',
    amountType === 'FREE_GIFTS',
    isPromoValid,
    promoCode,
  ]);

  // for Ab test
  useEffect(() => {
    if (!isPlanAbTestFired && isABTestFired && planAbTestName) {
      //@ts-ignore
      window?.dataLayer.push({
        event: 'step_2_event',
        ab_test_variant: planAbTestName,
      });
      stepTwoplanAbTestVar(true);
    }
  }, [isPlanAbTestFired, isABTestFired, planAbTestName]);

  useEffect(() => {
    if (new_order_flow) {
      isNewOrderFlowVar(true);
    }
  }, [new_order_flow]);

  // to set country
  useEffect(() => {
    if (countryCode) {
      localStorage.setItem('iso_alpha_3_code', JSON.stringify(countryCode)); // save country code to local storage on load step-1
    }

    if (countryCode && getServiceableCountries) {
      const selectedCountry = getServiceableCountries?.find(
        (country) => country.country_code === countryCode
      );
      setDefaultCountry(selectedCountry);
    }
  }, [countryCode, getServiceableCountries]);

  const updatePlan = (plan: number, planId: number) => {
    updateSelectedPlan(plan, planId);
  };

  const onClickCountry = (selectedCountryCode: string) => {
    // Ab test
    // localStorage.setItem('iso_alpha_3_code', JSON.stringify(countryCode));
    // if (countryCode === 'ind') {
    //   isABTestToFiredVar(false);
    // } else {
    //   planAbTestName && isABTestToFiredVar(true);
    // }
    if (selectedCountryCode === 'ind') {
      router.push(INDIA_BASE_URL);
    }

    if (selectedCountryCode !== countryCode) {
      subscriptionIdStateVar('');
    }

    router.push(`/${selectedCountryCode}${AppRoutes.ORDER_STEP_TWO}`);
  };

  const onVisible = (inView: boolean) => {
    if (inView) {
      setHeaderExpanded(false);
    } else {
      setHeaderExpanded(true);
    }
  };

  if (isLoading) {
    return <PageLoader />;
  }

  return (
    <>
      <RedirectToIndiaModal isStepTwo={true} />
      {getServiceableCountries ? (
        <CountryDrawer
          getServiceableCountries={getServiceableCountries}
          isCountryDrawerOpen={isCountryDrawerOpen}
          setCountryDrawerOpen={setCountryDrawerOpen}
          onClickCountry={onClickCountry}
        />
      ) : null}

      <Header
        plans={plans}
        frequencies={frequencies}
        isHeaderExpanded={isHeaderExpanded}
        defaultCountry={defaultCountry}
        setCountryDrawerOpen={setCountryDrawerOpen}
      />

      <InView threshold={0.5} onChange={(inView: boolean) => onVisible(inView)}>
        <PlanAndFreq
          plans={plans}
          frequencies={frequencies}
          updatePlan={updatePlan}
          countryCode={countryCode}
        />
      </InView>
      <ProductCategories
        categories={categories}
        filterData={getAllFilters}
        countryCode={countryCode}
        frequenciesV2={frequenciesV2}
        setLoading={setLoading}
      />
    </>
  );
};

export default CombinedOrderStepOneAndTwo;
